import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer, Select, InputNumber, Button, Tag, Spin, Empty, Tooltip, Popconfirm,
  Modal, Input, message as antMessage, Checkbox, Tabs, Alert, Avatar, Collapse,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined,
  CheckCircleFilled, ShareAltOutlined, SendOutlined, CarOutlined, CalculatorOutlined,
  ShopOutlined, UserOutlined, QuestionCircleOutlined, EyeOutlined, UndoOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { API_DOMAIN } from "../constants";
import { useSocket } from "../contexts/SocketContext";
import { _fetchQuickReplies, substituteQRText, loadConditionLabels, conditionLabel, loadSideLabels, sideLabel } from "./helpers";
import { UnavailablePartsModal } from "./UnavailablePartsModal";

dayjs.extend(utc);

// ── Module-level caches ───────────────────────────────────────────────────────

let _cachedSuppliersV2 = null;
let _suppliersFetchV2 = null;
function fetchSuppliersV2Once() {
  if (_cachedSuppliersV2) return Promise.resolve(_cachedSuppliersV2);
  if (_suppliersFetchV2) return _suppliersFetchV2;
  _suppliersFetchV2 = axios
    .post(`${API_DOMAIN}/whatsapp/get_suppliersV2`, {}, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    })
    .then(res => {
      _cachedSuppliersV2 = res.data.success ? (res.data.data || []) : [];
      return _cachedSuppliersV2;
    })
    .catch(() => { _suppliersFetchV2 = null; return []; });
  return _suppliersFetchV2;
}

let _cachedCostPrices = null;
let _costPricesFetch = null;
function fetchCostPricesOnce() {
  if (_cachedCostPrices?.length) return Promise.resolve(_cachedCostPrices);
  if (_costPricesFetch) return _costPricesFetch;
  _costPricesFetch = axios
    .post(`${API_DOMAIN}/whatsapp/get_costprices`, {}, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    })
    .then(res => {
      const rows = res.data?.data ?? res.data ?? [];
      const data = Array.isArray(rows) ? rows : [];
      if (data.length) _cachedCostPrices = data; // only cache non-empty results
      _costPricesFetch = null;
      return data;
    })
    .catch(() => { _costPricesFetch = null; return []; });
  return _costPricesFetch;
}

// ── Pricing helpers ───────────────────────────────────────────────────────────

function getTier(costPrices, price) {
  return costPrices.find(t => price >= t.MinPrice && price <= t.MaxPrice) ?? null;
}

// GP% = (SP - CP) / CP × 100  →  SP = CP × (1 + GP/100)
export function spFromGp(cp, gp) {
  if (cp <= 0) return cp;
  return Math.round(cp * (1 + gp / 100) * 100) / 100;
}
export function gpFromSp(cp, sp) {
  if (cp <= 0) return 0;
  return Math.round(((sp - cp) / cp) * 10000) / 100; // 2 dp
}

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = { supplier_id: null, price: null, warranty_days: null, delivery_charge: null, gp: null, sp: null };

// Cost → recommended GP%/SP pair from the tier table (null gp/sp when cost is empty).
function recommendedPricing(costPrices, cost) {
  if (cost == null) return { gp: null, sp: null };
  const tier = getTier(costPrices, cost);
  const gp = tier ? Number(tier.GP_Percentage) : 0;
  return { gp, sp: spFromGp(cost, gp) };
}
const TD_STYLE = { padding: "8px 4px", verticalAlign: "middle" };
const TH_STYLE = { fontSize: 10, color: "#aaa", fontWeight: 700, letterSpacing: "0.04em", padding: "6px 4px" };
// B2C / B2B quick reply IDs — update here if templates change
export const QR_IDS = { b2c: "6a37b64f495446fce3b7eaed", b2b: "6a37b687dc39cd11d9538bf1" };

// ── Main component ────────────────────────────────────────────────────────────

export function PriceUpdateDrawer({ card, open, onClose, userId, onPartsLoaded, authState, approvalDecidedTick, userMap }) {
  const headers = useMemo(() => ({ accessToken: localStorage.getItem("accessToken") }), []);
  const { socket: mainSocket } = useSocket();

  const [parts, setParts] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [costPrices, setCostPrices] = useState([]);
  const [costPricesLoading, setCostPricesLoading] = useState(false);

  // quotePricing: { [quoteId]: { sp, gp, _cp } }
  // _cp is stored to detect when quoted_price was changed on edit
  const [quotePricing, setQuotePricing] = useState({});

  const [addingPartId, setAddingPartId] = useState(null);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);

  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);

  const [selectingPartId, setSelectingPartId] = useState(null);
  const [taskCustomer, setTaskCustomer] = useState(null);
  const [taskCustomerType, setTaskCustomerType] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [unavailableNoticeParts, setUnavailableNoticeParts] = useState(null);
  const [resubmitModalOpen, setResubmitModalOpen] = useState(false);
  const [resubmitReason, setResubmitReason] = useState("");
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [withdrawing, setWithdrawing] = useState(false);

  const pendingApproval = approvalHistory.find(a => a.status === "pending") || null;
  const [previewOpen, setPreviewOpen] = useState(false);

  // This drawer is reachable from any status (WorkflowTaskDrawer's Parts tab
  // opens it universally). draft/waiting_for_price are the only "normal"
  // editing stages — anything past that (sent for confirmation, payment in
  // flight/done, cancelled, completed, parked) is an override worth flagging.
  const isOverrideStatus = card?.status && card.status !== "draft" && card.status !== "waiting_for_price";
  const [overrideConfirm, setOverrideConfirm] = useState(null); // { title, description, onConfirm } | null
  const [overrideNote, setOverrideNote] = useState("");

  // Wraps `action` so that outside the normal draft/waiting_for_price editing
  // stages, staff see a confirm+optional-note dialog before an override
  // (add/edit/delete/select quote, share quote) proceeds. `action` receives
  // the trimmed note (or null).
  const withOverrideGuard = (title, description, action) => {
    if (!isOverrideStatus) { action(null); return; }
    setOverrideNote("");
    setOverrideConfirm({ title, description, onConfirm: action });
  };

  // Keep parent badge accurate
  useEffect(() => {
    if (!open || !card?.id || !parts.length) return;
    onPartsLoaded?.(card.id, parts);
  }, [parts, card?.id, open]); // eslint-disable-line

  // Fetch parts + approval history on open
  useEffect(() => {
    if (!open || !card?.id) { setParts([]); setQuotePricing({}); setApprovalHistory([]); setTaskCustomer(null); setTaskCustomerType(null); return; }
    setTaskLoading(true);
    setParts([]);
    setQuotePricing({});
    setApprovalHistory([]);
    setTaskCustomer(null);
    setTaskCustomerType(null);
    setAddingPartId(null);
    setAddForm(EMPTY_FORM);
    setEditingQuoteId(null);
    setEditForm(EMPTY_FORM);
    axios
      .get(`${API_DOMAIN}/query_workflow/task/get?query_pk=${card.id}`, { headers })
      .then(res => {
        if (res.data.success) {
          const taskData = res.data.data ?? {};
          const taskObj = taskData.task ?? taskData;
          if (taskObj.customer) setTaskCustomer(taskObj.customer);
          const ct = taskObj.customer_type ?? taskObj.customer?.type ?? taskObj.customer?.customer_type ?? null;
          if (ct != null) setTaskCustomerType(ct);
          const rawParts = taskData.parts ?? taskObj.parts ?? [];
          setParts(rawParts.map(p => ({
            ...p,
            supplier_quotes: p.supplier_quotes ?? p.quotes ?? [],
          })));
        }
      })
      .catch(() => {})
      .finally(() => setTaskLoading(false));
    axios
      .get(`${API_DOMAIN}/queries/${card.id}/approvals`, { headers })
      .then(res => { if (res.data.success) setApprovalHistory(res.data.data ?? []); })
      .catch(() => {});
  }, [open, card?.id]); // eslint-disable-line

  // Fetch cost prices once (cached)
  useEffect(() => {
    if (_cachedCostPrices) { setCostPrices(_cachedCostPrices); return; }
    setCostPricesLoading(true);
    fetchCostPricesOnce().then(data => {
      setCostPrices(data);
      setCostPricesLoading(false);
    });
  }, []);

  // On load: restore GP% for already-selected quotes that have a saved selling_price
  // (that value is authoritative — it's what's actually persisted). Every other
  // quote — unselected, or not yet priced — gets a tier-lookup preview so every
  // row shows a sensible number immediately without waiting on "Calculate prices".
  // These previews are NOT persisted anywhere until their quote is selected.
  useEffect(() => {
    if (!parts.length) return;
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (next[q.id]) return; // don't clobber an existing (possibly edited) entry
          const cp = Number(q.quoted_price || 0);
          if (p.selected_quote_id === q.id && p.selling_price != null) {
            const sp = Number(p.selling_price);
            next[q.id] = { sp, gp: gpFromSp(cp, sp), _cp: cp };
          } else {
            const rec = recommendedPricing(costPrices, cp);
            next[q.id] = { sp: rec.sp, gp: rec.gp, _cp: cp };
          }
        });
      });
      return next;
    });
  }, [parts, costPrices]); // eslint-disable-line

  // Real-time socket updates from other users
  useEffect(() => {
    if (!mainSocket || !open || !card?.id) return;

    const handleQuotesUpdated = (data) => {
      if (data?.data?.task_id !== card.id) return;
      const { quote, event } = data.data;
      if (!quote) return;
      setParts(prev => prev.map(p => {
        if (p.id !== quote.query_part_id) return p;
        const qs = p.supplier_quotes ?? [];
        if (event === "quote_disabled") {
          return { ...p, supplier_quotes: qs.filter(q => q.id !== quote.id) };
        }
        const exists = qs.some(q => q.id === quote.id);
        return {
          ...p,
          supplier_quotes: exists
            ? qs.map(q => q.id === quote.id ? { ...q, ...quote } : q)
            : [...qs, quote],
        };
      }));
    };

    const handlePartsUpdated = (data) => {
      if (data?.data?.task_id !== card.id) return;
      const { part, event } = data.data;
      if (!part) return;
      if (event === "part_disabled") {
        setParts(prev => prev.filter(p => p.id !== part.id));
      } else {
        setParts(prev => prev.map(p => p.id === part.id ? { ...p, ...part } : p));
      }
    };

    mainSocket.on("query:quotes_updated", handleQuotesUpdated);
    mainSocket.on("query:parts_updated", handlePartsUpdated);
    return () => {
      mainSocket.off("query:quotes_updated", handleQuotesUpdated);
      mainSocket.off("query:parts_updated", handlePartsUpdated);
    };
  }, [mainSocket, open, card?.id]); // eslint-disable-line

  // Supplier list (cached)
  useEffect(() => {
    if (allSuppliers.length) return;
    fetchSuppliersV2Once().then(s => setAllSuppliers(s));
  }, []); // eslint-disable-line

  const supplierOptions = useMemo(() =>
    allSuppliers.map(s => ({
      value: s.id,
      label: s.name && s.name !== s.number ? s.name : s.number,
      name: s.name || "",
      number: s.number || "",
    })),
    [allSuppliers]
  );

  // Order-level delivery: based on total cost of all selected quotes
  const orderDelivery = useMemo(() => {
    if (!costPrices.length) return null;
    const total = parts.reduce((sum, p) => {
      if (!p.selected_quote_id) return sum;
      const q = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
      return sum + Number(q?.quoted_price || 0);
    }, 0);
    if (total === 0) return null;
    const tier = getTier(costPrices, total);
    return tier ? Number(tier.DeliveryCost) : null;
  }, [parts, costPrices]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleAddQuote = async (partId, note) => {
    if (!addForm.supplier_id || addForm.price == null) return;
    setAddSaving(true);
    try {
      const res = await axios.post(
        `${API_DOMAIN}/query_workflow/update_part_price`,
        {
          query_part_id: partId,
          supplier_id: addForm.supplier_id,
          price_received: addForm.price,
          warranty_days: addForm.warranty_days ?? 0,
          delivery_charge: addForm.delivery_charge ?? 0,
          userId,
          ...(note ? { remarks: note } : {}),
        },
        { headers }
      );
      if (res.data.success) {
        const newQuote = res.data.data.quote;
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, supplier_quotes: [...(p.supplier_quotes ?? []), newQuote] }
            : p
        ));
        // The form already computed/holds the GP%/SP pair (auto-calculated from
        // cost, or manually adjusted) — carry it over as-is rather than
        // recomputing, so a manual tweak made just before saving isn't lost.
        const cp = Number(newQuote.quoted_price || addForm.price || 0);
        const gp = addForm.gp ?? 0;
        const sp = addForm.sp ?? cp;
        setQuotePricing(prev => ({ ...prev, [newQuote.id]: { sp, gp, _cp: cp } }));
        setAddingPartId(null);
        setAddForm(EMPTY_FORM);
      }
    } catch {}
    setAddSaving(false);
  };

  const handleEditQuote = async (quoteId, partId, note) => {
    if (editForm.price == null) return;
    setEditSaving(true);
    try {
      const res = await axios.patch(
        `${API_DOMAIN}/supplier-quotes/${quoteId}`,
        {
          supplier_id: editForm.supplier_id,
          quoted_price: editForm.price,
          delivery_cost: editForm.delivery_charge ?? 0,
          warranty_days: editForm.warranty_days ?? 0,
          userId,
          ...(note ? { remarks: note } : {}),
        },
        { headers }
      );
      if (res.data.success) {
        const updated = res.data.data;
        setParts(prev => prev.map(p => ({
          ...p,
          supplier_quotes: (p.supplier_quotes ?? []).map(q =>
            q.id === quoteId ? { ...q, ...updated } : q
          ),
        })));
        // Same rule as add: trust the form's own GP%/SP rather than recomputing.
        const newCp = Number(updated.quoted_price || editForm.price || 0);
        const gp = editForm.gp ?? 0;
        const sp = editForm.sp ?? newCp;
        setQuotePricing(prev => ({ ...prev, [quoteId]: { sp, gp, _cp: newCp } }));
        // If this quote is already the part's selected quote, its selling price
        // drives what the customer sees — persist it now, otherwise editing an
        // already-selected quote's price silently never reaches query_parts.
        const part = parts.find(p => p.id === partId);
        if (part?.selected_quote_id === quoteId) patchSellingPrice(partId, quoteId, sp);
        setEditingQuoteId(null);
        setEditForm(EMPTY_FORM);
      }
    } catch {}
    setEditSaving(false);
  };

  const onDeleteQuote_original = async (quoteId, partId) => {
    try {
      const res = await axios.patch(
        `${API_DOMAIN}/supplier-quotes/${quoteId}`,
        { quote_status: "disabled", userId },
        { headers }
      );
      if (res.data.success) {
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, supplier_quotes: (p.supplier_quotes ?? []).filter(q => q.id !== quoteId) }
            : p
        ));
      }
    } catch {}
  };

  const handleSelectQuote = async (partId, quoteId) => {
    setSelectingPartId(partId);
    try {
      const part = parts.find(p => p.id === partId);
      const newQuoteId = part?.selected_quote_id === quoteId ? null : quoteId;
      const sellingPrice = newQuoteId ? (quotePricing[newQuoteId]?.sp ?? null) : null;
      const res = await axios.patch(
        `${API_DOMAIN}/query-parts/${partId}`,
        { selected_quote_id: newQuoteId, selling_price: sellingPrice, userId },
        { headers }
      );
      if (res.data.success) {
        setParts(prev => prev.map(p =>
          p.id === partId
            ? { ...p, selected_quote_id: newQuoteId, selling_price: sellingPrice }
            : p
        ));
      }
    } catch {}
    setSelectingPartId(null);
  };

  // Count quotes not yet priced (to drive the button label)
  const unpricedCount = useMemo(() =>
    parts.reduce((n, p) =>
      n + (p.supplier_quotes ?? []).filter(q => !quotePricing[q.id]).length, 0),
    [parts, quotePricing]
  );

  const patchSellingPrice = (partId, quoteId, sp) => {
    axios.patch(
      `${API_DOMAIN}/query-parts/${partId}`,
      { selected_quote_id: quoteId, selling_price: sp, userId },
      { headers }
    ).then(res => {
      if (res.data.success) {
        setParts(prev => prev.map(p =>
          p.id === partId ? { ...p, selling_price: sp } : p
        ));
      }
    }).catch(() => {});
  };

  const handleCalculateAll = () => {
    if (!costPrices.length) return;
    const toSave = []; // [{ partId, quoteId, sp }] for already-selected quotes
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (next[q.id]) return; // skip already-priced (preserve manual edits)
          const cp = Number(q.quoted_price || 0);
          const tier = getTier(costPrices, cp);
          const gp = tier ? Number(tier.GP_Percentage) : 0;
          const sp = spFromGp(cp, gp);
          next[q.id] = { sp, gp, _cp: cp };
          if (p.selected_quote_id === q.id) toSave.push({ partId: p.id, quoteId: q.id, sp });
        });
      });
      return next;
    });
    toSave.forEach(({ partId, quoteId, sp }) => patchSellingPrice(partId, quoteId, sp));
  };

  const handleRoundAll = () => {
    const toSave = [];
    setQuotePricing(prev => {
      const next = { ...prev };
      parts.forEach(p => {
        (p.supplier_quotes ?? []).forEach(q => {
          if (!next[q.id]) return; // only round already-priced quotes
          const rounded = Math.round(next[q.id].sp);
          const cp = next[q.id]._cp ?? Number(q.quoted_price || 0);
          next[q.id] = { ...next[q.id], sp: rounded, gp: gpFromSp(cp, rounded) };
          if (p.selected_quote_id === q.id) toSave.push({ partId: p.id, quoteId: q.id, sp: rounded });
        });
      });
      return next;
    });
    toSave.forEach(({ partId, quoteId, sp }) => patchSellingPrice(partId, quoteId, sp));
  };

  const refreshApprovalHistory = () => {
    if (!card?.id) return;
    axios
      .get(`${API_DOMAIN}/queries/${card.id}/approvals`, { headers })
      .then(res => { if (res.data.success) setApprovalHistory(res.data.data ?? []); })
      .catch(() => {});
  };

  // Refresh history when admin approves or rejects (socket tick from QuerysetV4)
  useEffect(() => {
    if (approvalDecidedTick > 0 && open) refreshApprovalHistory();
  }, [approvalDecidedTick]); // eslint-disable-line

  const handleWithdrawApproval = async () => {
    if (!pendingApproval) return;
    setWithdrawing(true);
    try {
      await axios.delete(
        `${API_DOMAIN}/queries/approvals/${pendingApproval.id}`,
        { data: { userId }, headers }
      );
      setApprovalHistory(prev => prev.filter(a => a.id !== pendingApproval.id));
      antMessage.success("Approval request withdrawn");
    } catch (e) {
      antMessage.error(e?.response?.data?.message || "Failed to withdraw approval request");
    } finally {
      setWithdrawing(false);
    }
  };

  const handleClose = () => {
    setAddingPartId(null);
    setAddForm(EMPTY_FORM);
    setEditingQuoteId(null);
    setEditForm(EMPTY_FORM);
    onClose();
  };

  const v = card?.vehicle;
  const vehicleLabel = v
    ? [v.brand_name || v.brand, v.model_name || v.model, v.year ? `(${v.year})` : ""].filter(Boolean).join(" ")
    : null;
  const customerPhone = card?.customer?.customer_mobile || card?.customer_mobile || null;

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              Price Update{card?.query_id ? ` — #${card.query_id}` : ""}
            </div>
            {vehicleLabel && (
              <div style={{ fontSize: 12, color: "#888", fontWeight: 400, marginTop: 2 }}>{vehicleLabel}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {unpricedCount > 0 && (
              <Tooltip title={`Fill GP% and selling price for ${unpricedCount} unpriced quote${unpricedCount !== 1 ? "s" : ""} using cost tier lookup`}>
                <Button
                  icon={<CalculatorOutlined />}
                  size="small"
                  loading={costPricesLoading}
                  disabled={costPricesLoading}
                  onClick={handleCalculateAll}
                  style={{ flexShrink: 0 }}
                >
                  Calculate prices ({unpricedCount})
                </Button>
              </Tooltip>
            )}
            {Object.keys(quotePricing).length > 0 && (
              <Tooltip title="Round all selling prices to nearest whole number">
                <Button
                  icon={<span style={{ fontWeight: 700, fontSize: 11 }}>.0</span>}
                  size="small"
                  onClick={handleRoundAll}
                  style={{ flexShrink: 0 }}
                >
                  Round prices
                </Button>
              </Tooltip>
            )}
            {customerPhone && (
              <Tooltip title={pendingApproval ? "A quote is already awaiting admin approval — withdraw it first to submit a new one" : "Share quote to customer"}>
                <Button
                  icon={<ShareAltOutlined />}
                  size="small"
                  disabled={!!pendingApproval}
                  onClick={() => withOverrideGuard(
                    "Share quote after payment?",
                    "This query already has a payment in progress or completed. Sharing a new quote will submit it through the normal customer-approval flow again.",
                    () => {
                      const hasApproved = approvalHistory.some(a => a.status === "approved");
                      if (hasApproved) { setResubmitReason(""); setResubmitModalOpen(true); }
                      else setShareOpen(true);
                    }
                  )}
                  style={{ flexShrink: 0 }}
                >
                  Share quote
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      }
      open={open}
      onClose={handleClose}
      width={780}
      destroyOnHidden
      styles={{ body: { padding: 16, overflowY: "auto" } }}
    >
      {approvalHistory.length > 0 && (() => {
        const latest = approvalHistory[0];
        const isPending = latest.status === "pending";
        const isRejected = latest.status === "rejected";
        const deciderName = latest.decided_by ? (userMap?.[latest.decided_by] ?? `User #${latest.decided_by}`) : null;
        const older = approvalHistory.slice(1);
        return (
          <div style={{ marginBottom: 12 }}>
            <Alert
              type={isPending ? "warning" : isRejected ? "error" : "success"}
              showIcon
              message={
                isPending ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ fontSize: 12 }}>
                      Quote pending admin approval since{" "}
                      <strong>{dayjs.utc(latest.submitted_at.replace(" ", "T")).format("DD/MM/YYYY HH:mm")}</strong>
                      {" "}— you cannot submit another until this is decided or withdrawn.
                      {latest.resubmission_reason && latest.resubmission_reason !== "null" && (
                        <div style={{ marginTop: 4, color: "#d48806" }}>
                          Resubmit reason: {latest.resubmission_reason}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>View quote</Button>
                      <Popconfirm
                        title="Withdraw approval request?"
                        description="This cannot be undone. You can re-submit after withdrawing."
                        onConfirm={handleWithdrawApproval}
                        okText="Withdraw"
                        okButtonProps={{ danger: true }}
                        cancelText="Keep"
                      >
                        <Button size="small" danger loading={withdrawing}>Withdraw</Button>
                      </Popconfirm>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    <strong>{isRejected ? "Quote rejected" : "Quote approved"}</strong>
                    {deciderName && <> by <strong>{deciderName}</strong></>}
                    {isRejected && latest.decision_reason && <> — {latest.decision_reason}</>}
                    {isRejected && <> · You can now update and re-submit for approval.</>}
                    {latest.resubmission_reason && latest.resubmission_reason !== "null" && (
                      <div style={{ marginTop: 4, color: "#d48806" }}>
                        Resubmit reason: {latest.resubmission_reason}
                      </div>
                    )}
                  </div>
                )
              }
            />
            {older.length > 0 && (
              <Collapse
                ghost
                size="small"
                style={{ marginTop: 2 }}
                items={[{
                  key: "hist",
                  label: <span style={{ fontSize: 11, color: "#8c8c8c" }}>Previous approvals ({older.length})</span>,
                  children: (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {older.map(a => {
                        const dName = a.decided_by ? (userMap?.[a.decided_by] ?? `User #${a.decided_by}`) : null;
                        const aRejected = a.status === "rejected";
                        const aApproved = a.status === "approved";
                        return (
                          <div key={a.id} style={{
                            fontSize: 11, padding: "6px 10px", borderRadius: 4,
                            background: aRejected ? "#fff2f0" : aApproved ? "#f6ffed" : "#fffbe6",
                            border: `1px solid ${aRejected ? "#ffccc7" : aApproved ? "#b7eb8f" : "#ffe58f"}`,
                          }}>
                            <span style={{ fontWeight: 600, color: aRejected ? "#cf1322" : aApproved ? "#389e0d" : "#d48806" }}>
                              {aRejected ? "Rejected" : aApproved ? "Approved" : "Pending"}
                            </span>
                            {dName && <span style={{ color: "#595959" }}> by {dName}</span>}
                            {a.decided_at && (
                              <span style={{ color: "#8c8c8c" }}>
                                {" "}· {dayjs.utc(a.decided_at.replace(" ", "T")).format("DD MMM · HH:mm")}
                              </span>
                            )}
                            {a.resubmission_reason && a.resubmission_reason !== "null" && (
                              <div style={{ color: "#d48806", marginTop: 2 }}>Resubmit reason: {a.resubmission_reason}</div>
                            )}
                            {a.decision_reason && (
                              <div style={{ color: "#8c8c8c", marginTop: 2 }}>Decision: {a.decision_reason}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ),
                }]}
              />
            )}
          </div>
        );
      })()}
      {taskLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin /></div>
      ) : parts.length === 0 ? (
        <Empty description="No parts found" style={{ marginTop: 48 }} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {parts.map(part => (
            <PartSection
              key={part.id}
              part={part}
              supplierOptions={supplierOptions}
              quotePricing={quotePricing}
              costPrices={costPrices}
              addingPartId={addingPartId}
              setAddingPartId={setAddingPartId}
              addForm={addForm}
              setAddForm={setAddForm}
              addSaving={addSaving}
              onAddQuote={(partId) => withOverrideGuard(
                "Add quote after payment?",
                "This query already has a payment in progress or completed. Adding a new quote here is a record-keeping override.",
                (note) => handleAddQuote(partId, note)
              )}
              editingQuoteId={editingQuoteId}
              setEditingQuoteId={setEditingQuoteId}
              editForm={editForm}
              setEditForm={setEditForm}
              editSaving={editSaving}
              onEditSave={(quoteId, partId) => withOverrideGuard(
                "Edit quote after payment?",
                "This query already has a payment in progress or completed. Editing this quote is a record-keeping override.",
                (note) => handleEditQuote(quoteId, partId, note)
              )}
              onDeleteQuote={(quoteId, partId) => withOverrideGuard(
                "Delete quote after payment?",
                "This query already has a payment in progress or completed.",
                () => onDeleteQuote_original(quoteId, partId)
              )}
              onSelectQuote={(partId, quoteId) => withOverrideGuard(
                "Change selected quote after payment?",
                "This query already has a payment in progress or completed. This changes which quote is used for pricing.",
                () => handleSelectQuote(partId, quoteId)
              )}
              selectingPartId={selectingPartId}
            />
          ))}

          {/* Order-level delivery charge */}
          {orderDelivery != null && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 14px", borderRadius: 8,
              background: "#f6ffed", border: "1px solid #b7eb8f",
              fontSize: 13,
            }}>
              <CarOutlined style={{ color: "#52c41a" }} />
              <span style={{ color: "#595959" }}>Order delivery charge:</span>
              <span style={{ fontWeight: 700, color: "#389e0d" }}>AED {orderDelivery.toLocaleString()}</span>
              <span style={{ fontSize: 11, color: "#8c8c8c", marginLeft: 4 }}>(based on total selected cost)</span>
            </div>
          )}
        </div>
      )}

      <Modal
        open={resubmitModalOpen}
        title="Reason for new quote"
        okText="Continue"
        okButtonProps={{ disabled: !resubmitReason.trim() }}
        onOk={() => { setResubmitModalOpen(false); setShareOpen(true); }}
        onCancel={() => setResubmitModalOpen(false)}
        width={420}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>
          A quote for this query was already approved. Please explain why you are submitting a new one — the admin will see this reason.
        </p>
        <Input.TextArea
          autoFocus
          rows={3}
          maxLength={500}
          showCount
          placeholder="e.g. Wrong part was quoted, customer requested a different option..."
          value={resubmitReason}
          onChange={e => setResubmitReason(e.target.value)}
        />
      </Modal>

      <ShareQuoteModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        card={card}
        taskCustomer={taskCustomer}
        taskCustomerType={taskCustomerType}
        parts={parts}
        quotePricing={quotePricing}
        orderDelivery={orderDelivery}
        authState={authState}
        userId={userId}
        customerPhone={customerPhone}
        resubmissionReason={resubmitReason || null}
        onApprovalCreated={refreshApprovalHistory}
        onSuggestUnavailableNotice={setUnavailableNoticeParts}
      />

      <ShareQuoteModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        card={card}
        parts={parts}
        quotePricing={quotePricing}
        orderDelivery={orderDelivery}
        authState={authState}
        userId={userId}
        customerPhone={customerPhone}
        readOnly
        preloadedPayload={pendingApproval?.payload}
      />

      <UnavailablePartsModal
        open={!!unavailableNoticeParts}
        queryPk={card?.id}
        queryId={card?.query_id}
        customerName={taskCustomer?.customer_name || card?.customer?.customer_name}
        customerPhone={customerPhone}
        unavailableParts={unavailableNoticeParts || []}
        userId={userId}
        onClose={() => setUnavailableNoticeParts(null)}
        onSuccess={() => setUnavailableNoticeParts(null)}
      />

      <Modal
        open={!!overrideConfirm}
        title={overrideConfirm?.title}
        okText="Continue"
        okButtonProps={{ danger: true }}
        onOk={() => { const c = overrideConfirm; setOverrideConfirm(null); c?.onConfirm(overrideNote.trim() || null); }}
        onCancel={() => setOverrideConfirm(null)}
        width={440}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>{overrideConfirm?.description}</p>
        <Input.TextArea
          rows={2}
          maxLength={300}
          showCount
          placeholder="Optional note (e.g. price matched competitor)"
          value={overrideNote}
          onChange={e => setOverrideNote(e.target.value)}
        />
      </Modal>
    </Drawer>
  );
}

// ── Part section ──────────────────────────────────────────────────────────────

function PartSection({
  part, supplierOptions, quotePricing, costPrices,
  addingPartId, setAddingPartId, addForm, setAddForm, addSaving, onAddQuote,
  editingQuoteId, setEditingQuoteId, editForm, setEditForm, editSaving,
  onEditSave, onDeleteQuote, onSelectQuote, selectingPartId,
}) {
  const quotes = part.supplier_quotes ?? [];
  const isAdding = addingPartId === part.id;
  const isSelecting = selectingPartId === part.id;

  return (
    <div style={{ border: "1px solid #e8e8e8", borderRadius: 8, overflow: "hidden" }}>
      {/* Part header */}
      <div style={{
        background: "#f8f9fb", padding: "9px 14px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderBottom: "1px solid #e8e8e8",
      }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{part.part_name}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {part.selected_quote_id && (
            <span style={{ fontSize: 11, color: "#389e0d", fontWeight: 600 }}>
              <CheckCircleFilled style={{ marginRight: 4 }} />
              Quote selected
              {part.selling_price != null && (
                <span style={{ marginLeft: 6, fontWeight: 700 }}>
                  · AED {Number(part.selling_price).toLocaleString()}
                </span>
              )}
            </span>
          )}
          <Tag color={quotes.length > 0 ? "blue" : "default"} style={{ margin: 0 }}>
            {quotes.length} quote{quotes.length !== 1 ? "s" : ""}
          </Tag>
        </div>
      </div>

      {/* Quotes table */}
      {(quotes.length > 0 || isAdding) && (
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 28 }} />
            <col />
            <col style={{ width: 90 }} />
            <col style={{ width: 60 }} />
            <col style={{ width: 72 }} />
            <col style={{ width: 84 }} />
            <col style={{ width: 96 }} />
            <col style={{ width: 64 }} />
          </colgroup>
          <thead>
            <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
              <th style={{ ...TH_STYLE, paddingLeft: 14 }} />
              <th style={{ ...TH_STYLE, textAlign: "left" }}>SUPPLIER</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>COST</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>WARRANTY</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>DELIVERY</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>GP %</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>SELL PRICE</th>
              <th style={{ ...TH_STYLE, paddingRight: 14 }} />
            </tr>
          </thead>
          <tbody>
            {quotes.map(q =>
              editingQuoteId === q.id ? (
                <QuoteFormRow
                  key={q.id}
                  supplierOptions={supplierOptions}
                  costPrices={costPrices}
                  form={editForm} setForm={setEditForm}
                  saving={editSaving} saveDisabled={editForm.price == null}
                  onSave={() => onEditSave(q.id, part.id)}
                  onCancel={() => { setEditingQuoteId(null); setEditForm(EMPTY_FORM); }}
                  bg="#fffbe6"
                  isSelectedQuote={part.selected_quote_id === q.id}
                />
              ) : (
                <QuoteRow
                  key={q.id}
                  quote={q}
                  pricing={quotePricing[q.id] ?? null}
                  isSelected={part.selected_quote_id === q.id}
                  isSelecting={isSelecting}
                  onSelect={() => onSelectQuote(part.id, q.id)}
                  onEdit={() => {
                    setEditingQuoteId(q.id);
                    const cp = Number(q.quoted_price);
                    const existing = quotePricing[q.id];
                    const rec = existing ?? recommendedPricing(costPrices, cp);
                    setEditForm({
                      supplier_id: q.supplier_id,
                      price: cp,
                      warranty_days: q.warranty_days,
                      delivery_charge: Number(q.delivery_cost),
                      gp: rec.gp,
                      sp: rec.sp,
                    });
                  }}
                  onDelete={() => onDeleteQuote(q.id, part.id)}
                />
              )
            )}
            {isAdding && (
              <QuoteFormRow
                supplierOptions={supplierOptions}
                costPrices={costPrices}
                form={addForm} setForm={setAddForm}
                saving={addSaving} saveDisabled={!addForm.supplier_id || addForm.price == null}
                onSave={() => onAddQuote(part.id)}
                onCancel={() => { setAddingPartId(null); setAddForm(EMPTY_FORM); }}
                bg="#f0f9ff"
                isSelectedQuote={false}
              />
            )}
          </tbody>
        </table>
      )}

      {/* Add quote button */}
      {!isAdding && (
        <div style={{ padding: "10px 14px", borderTop: quotes.length > 0 ? "1px solid #f0f0f0" : "none" }}>
          <Button size="small" type="dashed" icon={<PlusOutlined />} block
            onClick={() => { setAddingPartId(part.id); setAddForm(EMPTY_FORM); }}
          >
            Add Quote
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Quote row (tr) ────────────────────────────────────────────────────────────

function QuoteRow({ quote, pricing, isSelected, isSelecting, onSelect, onEdit, onDelete }) {
  const supplierLabel = quote.supplier_name && quote.supplier_name !== quote.supplier_number
    ? quote.supplier_name : quote.supplier_number;
  const showNumber = quote.supplier_name && quote.supplier_name !== quote.supplier_number;
  const cp = Number(quote.quoted_price || 0);

  return (
    <tr style={{ background: isSelected ? "#f6ffed" : undefined, transition: "background 0.2s" }}>
      <td style={{ ...TD_STYLE, paddingLeft: 14 }}>
        <Tooltip title={isSelected ? "Deselect" : "Select this quote"}>
          <button
            onClick={onSelect}
            disabled={isSelecting}
            style={{
              width: 20, height: 20, borderRadius: "50%",
              border: "none", background: "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: isSelecting ? "default" : "pointer",
              padding: 0, outline: "none", opacity: isSelecting ? 0.5 : 1,
            }}
          >
            {isSelected
              ? <CheckCircleFilled style={{ fontSize: 18, color: "#52c41a" }} />
              : <span style={{ width: 18, height: 18, borderRadius: "50%", border: "1.5px solid #d9d9d9", display: "block" }} />
            }
          </button>
        </Tooltip>
      </td>
      <td style={TD_STYLE}>
        <div style={{ fontWeight: 500, fontSize: 12 }}>{supplierLabel}</div>
        {showNumber && <div style={{ fontSize: 10, color: "#bbb" }}>{quote.supplier_number}</div>}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontWeight: 600, fontSize: 12 }}>{cp.toLocaleString()}</td>
      <td style={{ ...TD_STYLE, textAlign: "right", color: "#666", fontSize: 12 }}>
        {quote.warranty_days ? `${quote.warranty_days}d` : "—"}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", color: "#666", fontSize: 12 }}>
        {Number(quote.delivery_cost) > 0 ? Number(quote.delivery_cost).toLocaleString() : "Free"}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", color: "#666", fontSize: 12 }}>
        {pricing?.gp != null ? `${(Math.round(pricing.gp * 100) / 100).toLocaleString()}%` : "—"}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontWeight: 600, fontSize: 12 }}>
        {pricing?.sp != null ? `AED ${(Math.round(pricing.sp * 100) / 100).toLocaleString()}` : "—"}
      </td>
      <td style={{ ...TD_STYLE, paddingRight: 14 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Tooltip title="Edit">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title="Delete this quote?" okText="Yes" cancelText="No" onConfirm={onDelete}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

// ── Quote form row (tr) — used for both add and edit ──────────────────────────

function QuoteFormRow({ supplierOptions, costPrices, form, setForm, onSave, onCancel, saving, saveDisabled, bg, isSelectedQuote }) {
  const priced = form.price != null;

  // Cost drives a full recompute every keystroke — always overrides whatever
  // GP%/SP held before, since cost is the one field allowed to touch both.
  const onCostChange = (v) => {
    const rec = recommendedPricing(costPrices, v);
    setForm(f => ({ ...f, price: v, gp: rec.gp, sp: rec.sp }));
  };
  // GP%/SP only affect each other — cost is never touched by either.
  const onGpChange = (v) => {
    if (!priced) return;
    setForm(f => ({ ...f, gp: v, sp: v != null ? spFromGp(f.price, v) : f.sp }));
  };
  const onSpChange = (v) => {
    if (!priced) return;
    setForm(f => ({ ...f, sp: v, gp: v != null ? gpFromSp(f.price, v) : f.gp }));
  };
  const onResetPricing = () => {
    if (!priced) return;
    const rec = recommendedPricing(costPrices, form.price);
    setForm(f => ({ ...f, gp: rec.gp, sp: rec.sp }));
  };

  return (
    <>
      {!isSelectedQuote && (
        <tr style={{ background: bg }}>
          <td colSpan={8} style={{ padding: "2px 14px 6px" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 11, color: "#874d00", background: "#fffbe6",
              border: "1px solid #ffe58f", borderRadius: 4, padding: "4px 8px",
            }}>
              <InfoCircleOutlined style={{ fontSize: 11, flexShrink: 0 }} />
              Preview prices — GP% and Sell Price here aren't saved until this quote is selected as the customer's chosen quote.
            </div>
          </td>
        </tr>
      )}
      <tr style={{ background: bg }}>
      <td style={{ ...TD_STYLE, paddingLeft: 14 }} />
      <td style={TD_STYLE}>
        <Select
          showSearch placeholder="Supplier"
          value={form.supplier_id}
          options={supplierOptions}
          filterOption={(input, option) => {
            const q = input.toLowerCase();
            return (option.name || "").toLowerCase().includes(q) || (option.number || "").toLowerCase().includes(q);
          }}
          optionRender={opt => (
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>
                {opt.data.name && opt.data.name !== opt.data.number ? opt.data.name : opt.data.number}
              </div>
              {opt.data.name && opt.data.name !== opt.data.number && (
                <div style={{ fontSize: 11, color: "#aaa" }}>{opt.data.number}</div>
              )}
            </div>
          )}
          onChange={v => setForm(f => ({ ...f, supplier_id: v }))}
          size="small" style={{ width: "100%" }}
        />
      </td>
      <td style={TD_STYLE}>
        <InputNumber placeholder="Price" value={form.price}
          onChange={onCostChange}
          min={0} size="small" style={{ width: "100%" }} prefix="AED" controls={false} />
      </td>
      <td style={TD_STYLE}>
        <InputNumber placeholder="Days" value={form.warranty_days}
          onChange={v => setForm(f => ({ ...f, warranty_days: v }))}
          min={0} size="small" style={{ width: "100%" }} controls={false} />
      </td>
      <td style={TD_STYLE}>
        <InputNumber placeholder="Del." value={form.delivery_charge}
          onChange={v => setForm(f => ({ ...f, delivery_charge: v }))}
          min={0} size="small" style={{ width: "100%" }} prefix="AED" controls={false} />
      </td>
      <td style={TD_STYLE}>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <InputNumber
            placeholder="GP%" value={form.gp != null ? Math.round(form.gp * 100) / 100 : undefined}
            onChange={onGpChange}
            disabled={!priced}
            min={0} max={99} precision={2} size="small" style={{ width: "100%" }} suffix="%" controls={false}
          />
          {priced && (
            <Tooltip title="Reset to recommended GP%/price">
              <Button size="small" type="text" icon={<UndoOutlined />} onClick={onResetPricing} style={{ flexShrink: 0, padding: "0 2px" }} />
            </Tooltip>
          )}
        </div>
      </td>
      <td style={TD_STYLE}>
        <InputNumber
          placeholder="Sell price" value={form.sp != null ? Math.round(form.sp * 100) / 100 : undefined}
          onChange={onSpChange}
          disabled={!priced}
          min={0} precision={2} size="small" style={{ width: "100%" }} prefix="AED" controls={false}
        />
      </td>
      <td style={{ ...TD_STYLE, paddingRight: 14 }}>
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <Button size="small" icon={<CloseOutlined />} onClick={onCancel} />
          <Button size="small" type="primary" icon={<CheckOutlined />}
            loading={saving} disabled={saveDisabled} onClick={onSave} />
        </div>
      </td>
      </tr>
    </>
  );
}

// ── Share Quote Modal ─────────────────────────────────────────────────────────

export function injectPartsTemplate(templateText, card, selectedParts, deliveryPerPart) {
  const customerName = card?.customer?.customer_name || card?.customer_name || "";
  const queryId = card?.query_id || card?.id || "";

  // Substitute placeholder if value is non-empty; otherwise drop the entire line
  const replaceOrDrop = (text, placeholder, value) => {
    if (value) return text.replace(new RegExp(`\\{\\{${placeholder}\\}\\}`, "gi"), value);
    return text.replace(new RegExp(`[^\n]*\\{\\{${placeholder}\\}\\}[^\n]*\n?`, "gi"), "");
  };

  let result = templateText.replace(
    /\{\{#parts\}\}([\s\S]*?)\{\{\/parts\}\}/g,
    (_, block) =>
      selectedParts.map(({ part, quote, sp, vatPct, vatEnabled }) => {
        const basePrice = (sp ?? Number(quote.quoted_price || 0)) + deliveryPerPart;
        const finalPrice = Math.round(vatEnabled ? basePrice * (1 + vatPct / 100) : basePrice);
        const vatText = vatEnabled ? `incl. ${vatPct}% VAT` : "";
        const wd = Number(quote.warranty_days || 0);
        const warrantyText = wd > 0 ? `${wd} days replacement warranty` : "";

        let b = block;
        b = replaceOrDrop(b, "part_name",      part.part_name || "");
        b = replaceOrDrop(b, "side",            sideLabel(part.side || ""));
        b = replaceOrDrop(b, "partnumber",      part.part_number || "");
        b = replaceOrDrop(b, "part_condition",  conditionLabel(part.condition_preference ?? part.condition ?? part.part_condition ?? ""));
        b = replaceOrDrop(b, "qty",             part.quantity ? String(part.quantity) : "");
        // Price + VAT on same line — combine to avoid trailing space when VAT is off
        b = b.replace(
          /\{\{price\}\}(\s*)\{\{vat\}\}/gi,
          (_, sep) => vatText ? `${finalPrice.toLocaleString()}${sep}${vatText}` : finalPrice.toLocaleString()
        );
        b = b.replace(/\{\{price\}\}/gi, finalPrice.toLocaleString());
        b = b.replace(/\{\{vat\}\}/gi,   vatText);
        b = replaceOrDrop(b, "warranty", warrantyText);

        return b;
      }).join("")
  );

  result = result
    .replace(/\{\{name\}\}/gi,          customerName)
    .replace(/\{\{customer_name\}\}/gi, customerName)
    .replace(/\{\{query_id\}\}/gi,      String(queryId))
    .replace(/\{\{query_number\}\}/gi,  String(queryId));

  return result;
}

function ShareQuoteModal({ open, onClose, card, taskCustomer, taskCustomerType, parts, quotePricing, orderDelivery, authState, userId, customerPhone, readOnly, preloadedPayload, onApprovalCreated, resubmissionReason, onSuggestUnavailableNotice }) {
  const headers = useMemo(() => ({ accessToken: localStorage.getItem("accessToken") }), []);

  const [quickReplies, setQuickReplies] = useState([]);
  const [qrLoading, setQrLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("b2c");
  const [includedPartIds, setIncludedPartIds] = useState(new Set());
  const [partSp, setPartSp] = useState({}); // { [partId]: number } — overrideable SP per part
  const [partVat, setPartVat] = useState({}); // { [partId]: { pct: 5, enabled: true } }
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [deliveryAmount, setDeliveryAmount] = useState(0);
  const [messageText, setMessageText] = useState("");
  const [textEditEnabled, setTextEditEnabled] = useState(false);
  const [textManuallyEdited, setTextManuallyEdited] = useState(false);
  const [sending, setSending] = useState(false);
  // Bumped when the condition code→label map arrives so labels replace raw codes
  const [condVersion, setCondVersion] = useState(0);

  // customer_type is numeric: 0 = B2B, 1 = B2C, null = unknown
  // taskCustomerType is extracted from the full task fetch and is the most reliable source
  const rawType = readOnly
    ? (preloadedPayload?.customer_type ?? null)
    : (taskCustomerType ?? taskCustomer?.type ?? taskCustomer?.customer_type ?? card?.customer_type ?? card?.customer?.type ?? card?.customer?.customer_type ?? null);
  const isB2B = rawType === 0 || rawType === "0";
  const isB2C = rawType === 1 || rawType === "1";
  const typeKnown = isB2B || isB2C;

  useEffect(() => {
    if (!open) return;
    loadConditionLabels().then(() => setCondVersion(v => v + 1));
    loadSideLabels().then(() => setCondVersion(v => v + 1));
    if (readOnly && preloadedPayload) {
      // Hydrate from the stored payload snapshot
      const sp = {};
      const vat = {};
      const included = new Set();
      (preloadedPayload.parts ?? []).forEach(p => {
        sp[p.query_part_id] = p.sp;
        vat[p.query_part_id] = { pct: p.vat_pct ?? 5, enabled: p.vat_enabled ?? true };
        if (p.included) included.add(p.query_part_id);
      });
      setPartSp(sp);
      setPartVat(vat);
      setIncludedPartIds(included);
      setDeliveryEnabled(preloadedPayload.delivery_enabled ?? true);
      setDeliveryAmount(preloadedPayload.delivery_amount ?? 0);
      setActiveTab(preloadedPayload.template_type ?? "b2c");
      setMessageText(preloadedPayload.message_text ?? "");
      setTextManuallyEdited(true); // suppress any re-injection
      setTextEditEnabled(false);
      setSending(false);
      return;
    }
    setActiveTab(isB2B ? "b2b" : "b2c");
    setIncludedPartIds(new Set(parts.filter(p => p.selected_quote_id).map(p => p.id)));
    const sp = {};
    const vat = {};
    parts.forEach(p => {
      const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
      sp[p.id] = quotePricing[p.selected_quote_id]?.sp ?? Number(quote?.quoted_price || 0);
      vat[p.id] = { pct: 5, enabled: true };
    });
    setPartSp(sp);
    setPartVat(vat);
    setDeliveryEnabled(true);
    setDeliveryAmount(orderDelivery ?? 0);
    setTextEditEnabled(false);
    setTextManuallyEdited(false);
    setSending(false);
    if (quickReplies.length) return;
    setQrLoading(true);
    _fetchQuickReplies(userId).then(list => {
      setQuickReplies(list.filter(qr => !qr.type || qr.type === "text"));
      setQrLoading(false);
    });
  }, [open]); // eslint-disable-line

  const selectedPartsForInjection = useMemo(() => {
    if (readOnly && preloadedPayload) {
      return (preloadedPayload.parts ?? [])
        .filter(p => p.included)
        .map(p => ({
          part: { part_name: p.part_name, condition_preference: p.condition },
          quote: { warranty_days: p.warranty_days, quoted_price: p.cp },
          sp: p.sp,
          vatPct: p.vat_pct ?? 5,
          vatEnabled: p.vat_enabled ?? true,
        }));
    }
    return parts
      .filter(p => includedPartIds.has(p.id) && p.selected_quote_id)
      .map(p => {
        const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        const sp = partSp[p.id] ?? quotePricing[p.selected_quote_id]?.sp ?? Number(quote?.quoted_price || 0);
        const { pct = 5, enabled = true } = partVat[p.id] ?? {};
        return { part: p, quote, sp, vatPct: pct, vatEnabled: enabled };
      })
      .filter(({ quote }) => !!quote);
  }, [readOnly, preloadedPayload, parts, includedPartIds, partSp, partVat]); // eslint-disable-line

  const deliveryPerPart = deliveryEnabled && selectedPartsForInjection.length > 0
    ? (deliveryAmount || 0) / selectedPartsForInjection.length
    : 0;

  const getTemplate = (tab) => {
    const id = tab === "b2b" ? QR_IDS.b2b : QR_IDS.b2c;
    return quickReplies.find(qr => qr.id === id || qr._id === id);
  };

  useEffect(() => {
    if (!open || textManuallyEdited) return;
    const qr = getTemplate(activeTab);
    if (!qr) { setMessageText(""); return; }
    const base = substituteQRText(qr.text || "", authState);
    setMessageText(injectPartsTemplate(base, card, selectedPartsForInjection, deliveryPerPart));
  }, [activeTab, selectedPartsForInjection, quickReplies, open, deliveryPerPart, condVersion]); // eslint-disable-line

  const togglePart = (partId) => {
    setIncludedPartIds(prev => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId); else next.add(partId);
      return next;
    });
  };

  const updateVat = (partId, key, val) =>
    setPartVat(prev => ({ ...prev, [partId]: { ...(prev[partId] ?? { pct: 5, enabled: true }), [key]: val } }));

  const handleSend = async () => {
    if (selectedPartsForInjection.length === 0) {
      antMessage.warning("Select at least one part to include in the quote before sending.");
      return;
    }
    if (!messageText.trim() || !customerPhone) return;

    const partsPayload = parts
      .filter(p => p.selected_quote_id)
      .map(p => {
        const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        if (!quote) return null;
        const isIncluded = includedPartIds.has(p.id);
        const sp = partSp[p.id] ?? quotePricing[p.selected_quote_id]?.sp ?? Number(quote.quoted_price || 0);
        const cp = Number(quote.quoted_price || 0);
        const { pct = 5, enabled = true } = partVat[p.id] ?? {};
        const share = isIncluded ? deliveryPerPart : 0;
        const basePrice = sp + share;
        const finalPrice = Math.round(enabled ? basePrice * (1 + pct / 100) : basePrice);
        return {
          query_part_id: p.id,
          part_name: p.part_name || "",
          quote_id: p.selected_quote_id,
          included: isIncluded,
          sp,
          cp,
          condition: conditionLabel(p.condition_preference ?? p.condition ?? p.part_condition) || "New",
          warranty_days: Number(quote.warranty_days || 0),
          vat_enabled: enabled,
          vat_pct: pct,
          delivery_share: isIncluded ? Number(deliveryPerPart.toFixed(2)) : 0,
          final_price: finalPrice,
        };
      })
      .filter(Boolean);

    const payload = {
      query_pk: card?.id,
      query_id: card?.query_id,
      customer_name: card?.customer?.customer_name || card?.customer_name || "",
      customer_phone: customerPhone,
      customer_type: rawType,
      userId,
      submitted_by: userId,
      submitted_by_name: authState?.user?.name || authState?.user?.username || "",
      template_type: activeTab,
      message_text: messageText.trim(),
      message_customised: textManuallyEdited,
      delivery_enabled: deliveryEnabled,
      delivery_amount: deliveryEnabled ? (deliveryAmount || 0) : 0,
      parts: partsPayload,
      ...(resubmissionReason ? { resubmission_reason: resubmissionReason } : {}),
    };

    setSending(true);
    try {
      const res = await axios.post(
        `${API_DOMAIN}/queries/${card?.id}/share-quote`,
        payload,
        { headers }
      );
      if (!res.data.success) throw new Error(res.data.message);
      if (res.data.mode === "approval_required") {
        antMessage.info("Quote submitted for admin approval");
        onApprovalCreated?.();
      } else {
        antMessage.success("Quote sent to customer");
        // The customer just heard good news about some parts — nudge staff to
        // also tell them about any part nobody ever quoted, in the same beat.
        const zeroQuoteParts = parts.filter(p => (p.supplier_quotes ?? p.quotes ?? []).length === 0);
        if (zeroQuoteParts.length > 0 && onSuggestUnavailableNotice) {
          Modal.confirm({
            title: "Some parts still have no quotes",
            content: `${zeroQuoteParts.length} part(s) — ${zeroQuoteParts.map(p => p.part_name).join(", ")} — have no supplier quotes. Send the customer a message about them now? You can also do this later from the task drawer's "Notify Unavailable Parts" button.`,
            okText: "Send now",
            cancelText: "Not now",
            onOk: () => onSuggestUnavailableNotice(zeroQuoteParts),
          });
        }
      }
      onClose();
    } catch (e) {
      antMessage.error(e?.response?.data?.message || e?.message || "Failed to submit quote");
    } finally {
      setSending(false);
    }
  };

  const unquotedParts = readOnly ? [] : parts.filter(p => !p.selected_quote_id);
  const customerName = readOnly
    ? (preloadedPayload?.customer_name || "—")
    : (card?.customer?.customer_name || card?.customer_name || "—");
  const queryId = readOnly ? preloadedPayload?.query_id : (card?.query_id || card?.id);

  const leftCol = (
    <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
      {readOnly && (
        <Alert
          type="info"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message="This is a read-only preview of the quote sent for approval."
        />
      )}
      <div style={{ padding: "8px 10px", background: "#f5f5f5", borderRadius: 6, fontSize: 12, flexShrink: 0 }}>
        <div style={{ color: "#8c8c8c", marginBottom: 6 }}><strong style={{ color: "#262626" }}>Query:</strong> #{queryId}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size={32}
            icon={isB2B ? <ShopOutlined /> : isB2C ? <UserOutlined /> : <QuestionCircleOutlined />}
            style={{ backgroundColor: isB2B ? "#1677ff" : isB2C ? "#722ed1" : "#d48806", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#262626" }}>{customerName}</div>
            <div style={{ color: "#8c8c8c", fontSize: 11 }}>
              {isB2B ? "B2B · Garage" : isB2C ? "B2C · Retail" : "Type unknown"}
              {!readOnly && customerPhone ? ` · ${customerPhone}` : ""}
            </div>
          </div>
        </div>
      </div>

      {unquotedParts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message={`${unquotedParts.length} part${unquotedParts.length > 1 ? "s have" : " has"} no selected quote — excluded`}
        />
      )}

      {/* Order-level delivery */}
      <div style={{
        border: "1px solid #d9d9d9", borderRadius: 6, padding: "8px 10px",
        flexShrink: 0, background: deliveryEnabled ? "#e6f7ff" : undefined,
        borderColor: deliveryEnabled ? "#91caff" : "#d9d9d9",
        transition: "background 0.15s, border-color 0.15s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: deliveryEnabled ? 6 : 0 }}>
          <Checkbox checked={deliveryEnabled} disabled={readOnly} onChange={e => setDeliveryEnabled(e.target.checked)}>
            <span style={{ fontWeight: 600, fontSize: 12 }}>Delivery charge</span>
          </Checkbox>
        </div>
        {deliveryEnabled && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 24 }}>
            <InputNumber
              value={deliveryAmount}
              min={0}
              size="small"
              prefix="AED"
              disabled={readOnly}
              style={{ width: 110 }}
              onChange={v => setDeliveryAmount(v ?? 0)}
            />
            {selectedPartsForInjection.length > 1 && (
              <span style={{ fontSize: 11, color: "#595959" }}>
                ÷ {selectedPartsForInjection.length} parts
                {" = "}
                <strong>AED {(deliveryAmount / selectedPartsForInjection.length).toFixed(2)}/part</strong>
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ fontWeight: 600, fontSize: 12, color: "#595959", flexShrink: 0 }}>Parts to include</div>

      {(readOnly ? (preloadedPayload?.parts ?? []) : parts).map(p => {
        // Normalise field names — live parts use p.id, payload parts use p.query_part_id
        const partKey = readOnly ? p.query_part_id : p.id;
        const quote = readOnly ? null : (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        const hasQuote = readOnly ? true : !!quote;
        const checked = readOnly ? !!p.included : includedPartIds.has(partKey);
        const sp = readOnly ? (p.sp ?? 0) : (quote ? (quotePricing[quote.id]?.sp ?? Number(quote.quoted_price || 0)) : null);
        const cp = readOnly ? (p.cp ?? 0) : Number(quote?.quoted_price || 0);
        const { pct = 5, enabled = true } = readOnly
          ? { pct: p.vat_pct ?? 5, enabled: p.vat_enabled ?? true }
          : (partVat[partKey] ?? {});
        const warrantyDays = readOnly ? (p.warranty_days ?? 0) : Number(quote?.warranty_days || 0);
        const condition = readOnly
          ? (conditionLabel(p.condition) || "New")
          : (conditionLabel(p.condition_preference ?? p.condition ?? p.part_condition) || "New");

        return (
          <div
            key={partKey}
            style={{
              border: `1px solid ${checked && hasQuote ? "#b7eb8f" : hasQuote ? "#d9d9d9" : "#ffbb96"}`,
              borderRadius: 6,
              padding: "8px 10px",
              opacity: hasQuote ? 1 : 0.55,
              background: checked && hasQuote ? "#f6ffed" : undefined,
              flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: hasQuote && !readOnly ? "pointer" : "default" }}
              onClick={() => hasQuote && !readOnly && togglePart(partKey)}
            >
              <Checkbox checked={checked} disabled={!hasQuote || readOnly} onChange={() => {}} />
              <span style={{ fontWeight: 600, fontSize: 12, flex: 1 }}>{p.part_name}</span>
              {!hasQuote && <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>No quote</Tag>}
            </div>
            {hasQuote && (
              <div style={{ marginTop: 6, marginLeft: 24, fontSize: 11, color: "#595959", lineHeight: 1.7 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                  <InputNumber
                    value={readOnly ? sp : (partSp[partKey] ?? sp)}
                    min={0}
                    size="small"
                    prefix="AED"
                    disabled={readOnly}
                    style={{ width: 110, fontWeight: 600 }}
                    onChange={v => !readOnly && setPartSp(prev => ({ ...prev, [partKey]: v ?? 0 }))}
                  />
                  <Tooltip title={warrantyDays > 0 ? `${warrantyDays} days replacement warranty` : "No warranty"}>
                    <span style={{ color: "#8c8c8c", cursor: "default" }}>
                      {condition}
                      {warrantyDays > 0 ? ` · ${warrantyDays}d` : " · No warranty"}
                    </span>
                  </Tooltip>
                </div>
                {(() => {
                  const currentSp = readOnly ? sp : (partSp[partKey] ?? sp ?? 0);
                  const gp = gpFromSp(cp, currentSp);
                  const gpColor = gp > 0 ? "#389e0d" : "#cf1322";
                  return (
                    <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                      <Tooltip title="Cost price — what the supplier quoted us">
                        <span style={{ cursor: "default" }}>
                          CP: <strong style={{ color: "#262626" }}>AED {cp.toLocaleString()}</strong>
                        </span>
                      </Tooltip>
                      {" · "}
                      <Tooltip title="Gross profit margin based on current selling price">
                        <span style={{ cursor: "default" }}>
                          GP: <strong style={{ color: gpColor }}>{gp.toFixed(1)}%</strong>
                        </span>
                      </Tooltip>
                    </div>
                  );
                })()}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                  <Checkbox
                    checked={enabled}
                    disabled={readOnly}
                    onChange={e => !readOnly && updateVat(partKey, "enabled", e.target.checked)}
                    style={{ fontSize: 11 }}
                  >
                    VAT
                  </Checkbox>
                  <InputNumber
                    value={pct}
                    min={0} max={100}
                    size="small"
                    disabled={readOnly || !enabled}
                    suffix="%"
                    style={{ width: 70 }}
                    onChange={v => !readOnly && updateVat(partKey, "pct", v ?? 0)}
                  />
                  {sp != null && (deliveryPerPart > 0 || enabled) && (() => {
                    const effectiveSp = readOnly ? sp : (partSp[partKey] ?? sp);
                    const withDelivery = effectiveSp + (checked ? deliveryPerPart : 0);
                    const final = readOnly ? p.final_price : Math.round(enabled ? withDelivery * (1 + pct / 100) : withDelivery);
                    return (
                      <span style={{ fontSize: 11, color: "#595959", display: "flex", alignItems: "center", gap: 4 }}>
                        {checked && deliveryPerPart > 0 && (
                          <Tooltip title={`Delivery share added to this part (${deliveryAmount} ÷ ${selectedPartsForInjection.length} part${selectedPartsForInjection.length > 1 ? "s" : ""})`}>
                            <span style={{ color: "#1677ff", cursor: "default" }}>
                              +{deliveryPerPart % 1 === 0 ? deliveryPerPart : deliveryPerPart.toFixed(2)} del
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Final customer-facing price after SP + delivery + VAT">
                          <span style={{
                            color: "#389e0d", fontWeight: 700, fontSize: 13,
                            background: "#f6ffed", border: "1px solid #b7eb8f",
                            borderRadius: 4, padding: "1px 6px", cursor: "default",
                          }}>AED {final?.toLocaleString()}</span>
                        </Tooltip>
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const rightCol = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
      {!typeKnown && (
        <Alert
          type="info"
          showIcon
          style={{ fontSize: 11, flexShrink: 0 }}
          message="Customer type not set — select the appropriate template"
        />
      )}
      {!readOnly && (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ flexShrink: 0 }}
          items={[
            { key: "b2c", label: "B2C · Retail" },
            { key: "b2b", label: "B2B · Garage" },
          ]}
        />
      )}
      {!readOnly && qrLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>
      ) : !readOnly && !getTemplate(activeTab) ? (
        <Alert type="warning" showIcon message="Quick reply template not found for this customer type" />
      ) : (
        <>
          {!readOnly && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <Checkbox
                checked={textEditEnabled}
                onChange={e => {
                  setTextEditEnabled(e.target.checked);
                }}
              >
                <span style={{ fontSize: 12 }}>Edit message directly</span>
              </Checkbox>
              {textManuallyEdited && (
                <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
                  Customised — sent as-is, no re-injection
                </Tag>
              )}
              {textManuallyEdited && textEditEnabled && (
                <Tooltip title="Discard your edits and go back to auto-generated text">
                  <Button
                    size="small"
                    type="text"
                    style={{ fontSize: 11, color: "#8c8c8c", padding: "0 4px" }}
                    onClick={() => {
                      setTextManuallyEdited(false);
                      setTextEditEnabled(false);
                      const qr = getTemplate(activeTab);
                      if (qr) {
                        const base = substituteQRText(qr.text || "", authState);
                        setMessageText(injectPartsTemplate(base, card, selectedPartsForInjection, deliveryPerPart));
                      }
                    }}
                  >
                    Reset to template
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
          <Input.TextArea
            value={messageText}
            readOnly={readOnly || !textEditEnabled}
            onChange={e => {
              if (readOnly) return;
              setMessageText(e.target.value);
              setTextManuallyEdited(true);
            }}
            style={{
              fontSize: 12, fontFamily: "inherit", flex: 1, resize: "none", overflowY: "auto",
              background: !readOnly && textEditEnabled ? "#fff" : "#fafafa",
              cursor: !readOnly && textEditEnabled ? "text" : "default",
              color: !readOnly && textEditEnabled ? "#262626" : "#595959",
            }}
            placeholder="Message preview…"
          />
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={readOnly
        ? <span><EyeOutlined style={{ marginRight: 6 }} />Quote preview — pending approval</span>
        : <span><ShareAltOutlined style={{ marginRight: 6 }} />Share quote to customer</span>
      }
      style={{ top: 16, paddingBottom: 0 }}
      styles={{
        content: { height: "calc(100vh - 32px)", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
        header: { padding: "16px 24px", flexShrink: 0 },
        body: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", padding: "0 24px" },
        footer: { padding: "12px 24px", flexShrink: 0 },
      }}
      footer={
        readOnly ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              disabled={!messageText.trim()}
              onClick={handleSend}
            >
              Send to customer
            </Button>
          </div>
        )
      }
      width={860}
      destroyOnHidden
      zIndex={1050}
    >
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, flex: 1, minHeight: 0 }}>
        {leftCol}
        {rightCol}
      </div>
    </Modal>
  );
}
